/* eslint-disable react/require-default-props */
/* eslint-disable react/no-array-index-key */
/* eslint-disable @typescript-eslint/no-explicit-any */
import IconClose from '@assets/icons/close.svg?react';
import IconEmpty from '@assets/icons/empty.svg?react';
import {
 Box,
 debounce,
 FormControl,
 IconButton,
 InputAdornment,
 InputLabel,
 MenuItem,
 Popover,
 Select,
 TextField,
 useTheme,
} from '@mui/material';
import {
 KeyboardEvent,
 MutableRefObject,
 SetStateAction,
 useCallback,
 useMemo,
 useRef,
} from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { isWeightModelField } from '@/utils/structureUtils';
import DateRangeFilter from '../FilterSidebar/DateRangeFilter';
import SelectApi from '../SelectApi';
import { CustomGridApi } from '../XTable/types';
import useFilterMenu from './useFilterMenu';

const FilterMenu = ({
 filterMenuState,
 setOpen,
 apiRef,
}: {
 filterMenuState: {
  anchorElement: HTMLDivElement | HTMLButtonElement | null;
  colName: string;
 } | null;
 setOpen: SetStateAction<any>;
 apiRef: MutableRefObject<CustomGridApi>;
}) => {
 const th = useTheme();
 const { t } = useTranslation();
 const {
  currentFilter,
  handleChange,
  handleDelete,
  currentType,
  currentStructure,
  control,
  formState,
  anchorElement,
  translation,
  getValues,
  setValue,
  methods,
  colName,
 } = useFilterMenu({ filterMenuState, apiRef, setOpen });

 const inputStyles = useMemo(
  () => ({
   width: '100%',
   backgroundColor: th.palette.kemek.white,
   borderRadius: '4px',
   '& .MuiInputBase-root': {
    backgroundColor: th.palette.kemek.white,
   },
  }),
  [th.palette.kemek.white],
 );

 // Debounce the handleChange function
 const debouncedChange = useCallback(
  debounce((name, value) => {
   handleChange(name, value);
  }, 1000), // Adjust delay as needed
  [handleChange],
 );
 const menuContentRef = useRef<HTMLDivElement | null>(null);

 const handleMenuEntered = useCallback(() => {
  const focusableInput = menuContentRef.current?.querySelector(
   'input:not([disabled]), textarea:not([disabled]), [role="combobox"]:not([aria-disabled="true"])',
  ) as HTMLElement | null;

  focusableInput?.focus();
 }, []);

 const handleInputSubmit = useCallback(
  (event: KeyboardEvent<HTMLInputElement>) => {
   if (event.key !== 'Enter') {
    return;
   }

   event.preventDefault();
   debouncedChange.clear();
   const currentValue = getValues(currentStructure.Name) || '';
   handleChange(currentStructure.Name, currentValue);
   setOpen(false);
  },
  [currentStructure.Name, debouncedChange, handleChange, getValues, setOpen],
 );

 const returnEl = useMemo(() => {
  let returnObj = null;

  if (isWeightModelField(currentStructure)) {
   returnObj = (
    <Controller
     name={colName}
     control={control}
     render={({ field }) => (
      <TextField
       sx={inputStyles}
       {...field}
       value={field.value || ''}
       onChange={(e) => {
        const onlyNums = e.target.value.replace(/[^0-9]/g, '');
        field.onChange(onlyNums);
        debouncedChange(currentStructure.Name, onlyNums);
       }}
       onKeyDown={handleInputSubmit}
       slotProps={{
        input: {
         inputMode: 'numeric',
        },
       }}
       type="number"
       label={currentStructure.Translation}
       variant="outlined"
       error={!!formState?.errors?.[currentStructure.Name]}
       fullWidth
      />
     )}
    />
   );
  } else
   switch (currentType) {
    case 'model':
     returnObj = (
      <SelectApi
       name={colName}
       url={currentStructure.ForeignKeyUrl}
       filter={currentStructure.ForeignKeyDefaultFilter}
       label={translation}
       isEditable
       operator="contains"
       control={control}
       setValue={handleChange}
       multiple
       styles={{
        backgroundColor: th.palette.kemek.white,
       }}
      />
     );
     break;
    case 'array':
     returnObj = (
      <SelectApi
       name={colName}
       url={currentStructure.ForeignKeyUrl}
       filter={currentStructure.ForeignKeyDefaultFilter}
       label={translation}
       isEditable
       operator="contains"
       control={control}
       setValue={handleChange}
       multiple
      />
     );
     break;
    case 'datetime':
     returnObj = (
      <Box
       sx={{
        minWidth: 270,
        background: th.palette.kemek.white,
       }}
      >
       <FormProvider {...methods}>
        <DateRangeFilter
         fieldKey={colName}
         label={translation}
         onDateRangeChange={(dateFrom, dateTo) => {
          setValue('DateFrom', dateFrom ?? '');
          setValue('DateTo', dateTo ?? '');
          handleChange('DateFrom', dateFrom ?? '');
          handleChange('DateTo', dateTo ?? '');
         }}
        />
       </FormProvider>
      </Box>
     );
     break;
    case 'enum':
     returnObj = (
      <FormControl key={currentStructure.Name} sx={{ width: '100%' }}>
       <InputLabel htmlFor={currentStructure.Name}>{translation}</InputLabel>
       <Controller
        name={colName}
        control={control}
        render={({ field }) => (
         <Select
          {...field}
          id={currentStructure.Name}
          renderValue={(selected) =>
           currentStructure.EnumValues[selected] || 'Select an option'
          }
          onChange={(e: any, value: any) => {
           handleChange(currentStructure.Name, e.target.value || '');
          }}
          value={typeof field.value !== 'undefined' ? field.value : ''}
          label={translation}
          sx={inputStyles}
          endAdornment={
           field.value && (
            <InputAdornment
             sx={{ position: 'absolute', right: 32 }}
             position="end"
            >
             <IconButton
              title={t('global.clear')}
              aria-label={t('global.clear')}
              onClick={() => {
               handleChange(currentStructure.Name, '');
              }}
             >
              <IconClose style={{ width: 14, height: 14 }} />
             </IconButton>
            </InputAdornment>
           )
          }
         >
          {Object.entries(currentStructure.EnumValues).map(([value, label]) => (
           <MenuItem key={value} value={value}>
            {label}
           </MenuItem>
          ))}
         </Select>
        )}
       />
      </FormControl>
     );
     break;
    case 'boolean':
     returnObj = (
      <FormControl key={currentStructure.Name} sx={{ width: '100%' }}>
       <InputLabel htmlFor={currentStructure.Name}>{translation}</InputLabel>
       <Controller
        name={colName}
        control={control}
        render={({ field }) => (
         <Select
          {...field}
          id={currentStructure.Name}
          value={typeof field.value !== 'undefined' ? field.value : ''}
          label={translation}
          sx={{ ...inputStyles, paddingRight: 0 }} // why this padding appeared in a first place?
          onChange={(e: any) => {
           handleChange(currentStructure.Name, e.target.value ?? '');
          }}
          endAdornment={
           field.value && (
            <InputAdornment
             sx={{ position: 'absolute', right: 32 }}
             position="end"
            >
             <IconButton
              title={t('global.clear')}
              aria-label={t('global.clear')}
              onClick={() => {
               handleChange(currentStructure.Name, '');
              }}
             >
              <IconClose style={{ width: 14, height: 14 }} />
             </IconButton>
            </InputAdornment>
           )
          }
         >
          <MenuItem value="true">{t('global.yes')}</MenuItem>
          <MenuItem value="false">{t('global.no')}</MenuItem>
         </Select>
        )}
       />
      </FormControl>
     );
     break;
    default:
     returnObj = (
      <Controller
       name={colName}
       control={control}
       render={({ field }) => (
        <TextField
         sx={inputStyles}
         {...field}
         value={field.value || ''}
         onChange={(e) => {
          const value = e.target.value || '';
          field.onChange(value);
          debouncedChange(currentStructure.Name, value);
         }}
         onKeyDown={handleInputSubmit}
         label={currentStructure.Translation}
         variant="outlined"
         error={!!formState?.errors?.[currentStructure.Name]}
         fullWidth
        />
       )}
      />
     );
     break;
   }
  return returnObj;
 }, [currentFilter]);
 return (
  anchorElement && (
   <Popover
    id="limit-modal"
    open={!!anchorElement}
    anchorEl={anchorElement}
    onClose={() => setOpen(false)}
    anchorOrigin={{
     vertical: 'top',
     horizontal: 'center',
    }}
    transformOrigin={{
     vertical: 'bottom',
     horizontal: 'center',
    }}
    TransitionProps={{
     onEntered: handleMenuEntered,
    }}
   >
    <Box
     ref={menuContentRef}
     // component="form"
     // noValidate
     sx={{
      borderRadius: '4px',
      border: `1px solid ${th.palette.kemek.lightestGray}`,
      backgroundColor: th.palette.kemek.lightBlue,
      p: '8px',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
     }}
    >
     {returnEl ? (
      <Box
       sx={{
        width: '100%',
        display: 'flex',
        gap: '4px',
        alignItems: 'center',
       }}
      >
       <Box
        sx={{
         flex: 1,
         minWidth: 150,
         //  backgroundColor: th.palette.kemek.white,
         //  borderRadius: '4px',
        }}
       >
        {returnEl}
       </Box>

       <IconButton
        title={t('colModal.removeFilter')}
        aria-label={t('colModal.removeFilter')}
        onClick={handleDelete}
        sx={{ p: '4px', width: 24, height: 24 }}
       >
        <IconEmpty style={{ width: 18, height: 18 }} />
       </IconButton>
      </Box>
     ) : null}
    </Box>
   </Popover>
  )
 );
};

export default FilterMenu;
